// frontend/src/components/MutantStepNode.js
import React from 'react';
import './MutantStepNode.css';
import ReadableText from './ReadableText';
import { useI18n } from '../i18n';

const MutantStepNode = ({
    step,
    index,
    isDraft,
    title,
    text,
    isVisualActive,
    isLoading,
    isBackground,
    isSubstepsLoading,
    onToggleSubstep,
    onToggleComplete,
    onGenerateSubsteps,
    onToggleVisual,
    onExpandImage,
    backgroundJobs = {},
    isLast
}) => {
    const { t } = useI18n();
    const isCompleted = !!step.completed;
    const hasImage = !!step.imageUrl;
    const substeps = Array.isArray(step.substeps) ? step.substeps : [];

    return (
        <div className={`mutant-step-node ${isCompleted ? 'mutant-done' : ''} ${isLast ? 'mutant-last' : ''}`}>
            <div className="mutant-flow-connector">
                <div className={`mutant-dot ${isCompleted ? 'mutant-dot-done' : ''}`}>
                    {isCompleted && <span className="mutant-check">✓</span>}
                </div>
                <div className="mutant-line"></div>
            </div>

            <div className="mutant-step-body">
                <div className="mutant-step-header">
                    <div className="mutant-header-left">
                        <span className="mutant-index">#{index + 1}</span>
                        {title && <h4 className="mutant-title"><ReadableText text={title} label={t('tasks.readStepTitle')}>{title}</ReadableText></h4>}
                    </div>

                    <div className="mutant-header-actions">
                        {!isDraft && (
                            <button
                                className={`mutant-action-btn ${isSubstepsLoading ? 'mutant-btn-processing' : ''}`}
                                onClick={() => onGenerateSubsteps && onGenerateSubsteps(index)}
                                title={t('tasks.generateDetails')}
                                disabled={isSubstepsLoading}
                            >
                                {isSubstepsLoading ? '...' : '+'}
                            </button>
                        )}
                        {!isDraft && (
                            <button
                                className={`mutant-action-btn mutant-visual-toggle ${isVisualActive ? 'active' : ''} ${isLoading ? 'mutant-btn-processing' : ''}`}
                                onClick={() => onToggleVisual && onToggleVisual(index, step)}
                                title={t('tasks.generateVisualsAll')}
                                disabled={isLoading}
                            >
                                {isLoading ? '...' : '👁'}
                            </button>
                        )}
                        <button
                            className={`mutant-complete-btn ${isCompleted ? 'active' : ''}`}
                            onClick={() => onToggleComplete && onToggleComplete(index)}
                        >
                            {isCompleted ? t('common.done') : t('tasks.completeStep')}
                        </button>
                    </div>
                </div>

                <div className="mutant-content-wrapper">
                    <p className="mutant-text">
                        <ReadableText text={text} label={t('tasks.readTaskStep')}>{text}</ReadableText>
                    </p>

                    {isVisualActive && (
                        <div className="mutant-visual-area">
                            {hasImage ? (
                                <div className="mutant-image-container">
                                    <img
                                        src={step.imageUrl}
                                        alt={t('tasks.visualStepAlt', { index: index + 1 })}
                                        onClick={() => onExpandImage && onExpandImage(step.imageUrl)}
                                        className="mutant-image"
                                    />
                                    <div className="mutant-image-glitch"></div>
                                </div>
                            ) : (
                                <div className="mutant-placeholder">
                                    {isBackground || isLoading || backgroundJobs[index] || backgroundJobs[`${index}`] ? (
                                        <div className="mutant-loading-spinner"></div>
                                    ) : (
                                        <small className="mutant-placeholder-text">{t('tasks.activateVisualHint')}</small>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {substeps.length > 0 && (
                        <div className="mutant-substeps">
                            {substeps.map((sub, sIdx) => {
                                const isSubBackground = !!backgroundJobs[`${index}_${sIdx}`];
                                const isSubCompleted = typeof sub === 'string' ? false : !!sub.completed;
                                const substepText = typeof sub === 'string' ? sub : (sub?.text || '');
                                const subImageUrl = typeof sub === 'string' ? null : sub?.imageUrl;
                                return (
                                    <div key={sIdx} className={`mutant-substep-row ${isSubCompleted ? 'sub-done' : ''}`}>
                                        <div className="mutant-substep-connector"></div>
                                        <input
                                            type="checkbox"
                                            id={`sub-${index}-${sIdx}`}
                                            checked={isSubCompleted}
                                            onChange={() => onToggleSubstep && onToggleSubstep(index, sIdx)}
                                            disabled={isDraft}
                                        />
                                        <label htmlFor={`sub-${index}-${sIdx}`} className="mutant-substep-text">
                                            <ReadableText text={substepText} label={t('tasks.readSubstep')}>{substepText}</ReadableText>
                                            {subImageUrl ? (
                                                <div className="mutant-substep-visual">
                                                    <img
                                                        src={subImageUrl}
                                                        alt="substep visual"
                                                        onClick={() => onExpandImage && onExpandImage(subImageUrl)}
                                                    />
                                                </div>
                                            ) : isSubBackground ? (
                                                <div className="mutant-visual-area">
                                                    <div className="mutant-placeholder">
                                                        <div className="mutant-loading-spinner"></div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MutantStepNode;
